/**
 * WHAT: Azure AD group-to-role mapping management interface.
 * WHY: DAMA uses Azure AD groups for RBAC -- this component lets administrators
 *      create, edit, and sync group mappings that determine which users get
 *      which roles (DEV_ADMIN, ATTESTER, technology-specific DBAs, etc.).
 */

import React, { useState, useEffect } from 'react';
import API_CONFIG from '../config/api.config';
import { useSafeTimeout } from '../hooks';
import './AzureADGroups.css';

/**
 * WHAT: CRUD interface for Azure AD group mappings with membership sync.
 * WHY: Automates role assignment by linking Azure AD security groups to DAMA
 *      application roles, reducing manual user provisioning and ensuring
 *      consistent access control across the platform.
 */
function AzureADGroups() {
  // State management
  const [groups, setGroups] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Filter state
  const [filterRole, setFilterRole] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Selected group/form state
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [formData, setFormData] = useState({
    group_id: '',
    group_name: '',
    description: '',
    mapped_role_id: '',
    is_active: true,
    auto_provision: true,
    sync_enabled: true
  });

  // Sync state
  const [syncing, setSyncing] = useState(false);
  const [syncResults, setSyncResults] = useState(null);

  // Safe timeout hook for auto-dismissing messages
  const { setSafeTimeout } = useSafeTimeout();

  // Fetch groups on component mount
  useEffect(() => {
    fetchGroups();
    fetchRoles();
  }, [showInactive, filterRole]);

  // Fetch all groups
  const fetchGroups = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.append('active_only', !showInactive);
      if (filterRole) params.append('role_id', filterRole);

      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint(`azure-ad/groups/?${params.toString()}`)
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch groups: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setGroups(data.groups);
      } else {
        throw new Error(data.error || 'Failed to fetch groups');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch available roles from the API (replaces hardcoded list)
  const fetchRoles = async () => {
    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('users/roles/'));
      if (!response.ok) throw new Error('Failed to fetch roles');
      const data = await response.json();
      if (data.success) {
        setRoles(data.roles);
      }
    } catch (err) {
      console.error('Error fetching roles:', err);
      // Fallback to static list if API unavailable
      setRoles([
        { role_id: 'DEV_ADMIN', role_name: 'Developer Admin' },
        { role_id: 'DAMA_ADMIN', role_name: 'DAMA Campaign Admin' },
        { role_id: 'ORACLE_DB_ADMIN', role_name: 'Oracle DB Administrator' },
        { role_id: 'MSSQL_DB_ADMIN', role_name: 'MSSQL DB Administrator' },
        { role_id: 'MYSQL_DB_ADMIN', role_name: 'MySQL DB Administrator' },
        { role_id: 'CASSANDRA_DB_ADMIN', role_name: 'Cassandra DB Administrator' },
        { role_id: 'POSTGRESQL_DB_ADMIN', role_name: 'PostgreSQL DB Administrator' },
        { role_id: 'ATTESTER', role_name: 'Attester' }
      ]);
    }
  };

  // Fetch group members
  const fetchGroupMembers = async (groupId) => {
    try {
      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint(`azure-ad/groups/${groupId}/members/`)
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch members: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setGroupMembers(data.members);
        setShowMembersModal(true);
      } else {
        throw new Error(data.error || 'Failed to fetch members');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Create new group
  const handleCreateGroup = async (e) => {
    e.preventDefault();

    try {
      setError(null);

      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint('azure-ad/groups/create/'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        }
      );

      const data = await response.json();

      if (data.success) {
        setSuccessMessage(`Group created successfully: ${formData.group_name}`);
        setShowCreateModal(false);
        resetForm();
        fetchGroups();

        // Clear success message after 3 seconds
        setSafeTimeout(() => setSuccessMessage(null), 3000);
      } else {
        throw new Error(data.error || 'Failed to create group');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Update existing group
  const handleUpdateGroup = async (e) => {
    e.preventDefault();

    try {
      setError(null);

      const updateData = {
        group_name: formData.group_name,
        description: formData.description,
        mapped_role_id: formData.mapped_role_id,
        is_active: formData.is_active,
        auto_provision: formData.auto_provision,
        sync_enabled: formData.sync_enabled
      };

      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint(`azure-ad/groups/${selectedGroup.group_id}/update/`),
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updateData),
        }
      );

      const data = await response.json();

      if (data.success) {
        setSuccessMessage(`Group updated successfully: ${formData.group_name}`);
        setShowEditModal(false);
        resetForm();
        fetchGroups();

        setSafeTimeout(() => setSuccessMessage(null), 3000);
      } else {
        throw new Error(data.error || 'Failed to update group');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Delete group
  const handleDeleteGroup = async (hardDelete = false) => {
    try {
      setError(null);

      const params = hardDelete ? '?hard_delete=true' : '';
      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint(`azure-ad/groups/${selectedGroup.group_id}/delete/${params}`),
        {
          method: 'DELETE',
        }
      );

      const data = await response.json();

      if (data.success) {
        setSuccessMessage(data.message);
        setShowDeleteModal(false);
        setSelectedGroup(null);
        fetchGroups();

        setSafeTimeout(() => setSuccessMessage(null), 3000);
      } else {
        throw new Error(data.error || 'Failed to delete group');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Trigger group sync
  const handleTriggerSync = async (groupId = 'all') => {
    try {
      setSyncing(true);
      setError(null);
      setSyncResults(null);

      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint(`azure-ad/groups/${groupId}/sync/`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sync_memberships: true,
            sync_group_info: true
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setSyncResults(data.sync_results);
        setSuccessMessage('Group sync completed successfully');
        fetchGroups();

        setTimeout(() => {
          setSuccessMessage(null);
          setSyncResults(null);
        }, 5000);
      } else {
        throw new Error(data.error || 'Sync failed');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSyncing(false);
    }
  };

  // Helper functions
  const resetForm = () => {
    setFormData({
      group_id: '',
      group_name: '',
      description: '',
      mapped_role_id: '',
      is_active: true,
      auto_provision: true,
      sync_enabled: true
    });
    setSelectedGroup(null);
  };

  const openEditModal = (group) => {
    setSelectedGroup(group);
    setFormData({
      group_id: group.group_id,
      group_name: group.group_name,
      description: group.description || '',
      mapped_role_id: group.mapped_role_id,
      is_active: group.is_active,
      auto_provision: group.auto_provision,
      sync_enabled: group.sync_enabled
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (group) => {
    setSelectedGroup(group);
    setShowDeleteModal(true);
  };

  const openMembersModal = (group) => {
    setSelectedGroup(group);
    fetchGroupMembers(group.group_id);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getRoleBadgeClass = (roleId) => {
    if (!roleId) return 'role-badge';
    if (roleId === 'DEV_ADMIN') return 'role-badge dev-admin';
    if (roleId === 'DAMA_ADMIN') return 'role-badge uar-admin';
    if (roleId === 'ATTESTER') return 'role-badge attester';
    if (roleId.endsWith('_DB_ADMIN')) return 'role-badge dev-admin'; // DBA roles use similar styling
    return 'role-badge';
  };

  // Render loading state
  if (loading && groups.length === 0) {
    return (
      <div className="azure-ad-groups-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading groups...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="azure-ad-groups-container">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <h1>Azure AD Group Management</h1>
          <p className="header-subtitle">
            Manage Azure AD group-to-role mappings and memberships
          </p>
        </div>
        <div className="header-right">
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateModal(true)}
          >
            + Create Group Mapping
          </button>
          <button
            className="btn btn-sync"
            onClick={() => handleTriggerSync('all')}
            disabled={syncing}
          >
            {syncing ? 'Syncing...' : '🔄 Sync All Groups'}
          </button>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="alert alert-success">
          ✓ {successMessage}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="alert alert-error">
          ✗ {error}
        </div>
      )}

      {/* Sync Results */}
      {syncResults && (
        <div className="sync-results">
          <h3>Sync Results</h3>
          <div className="sync-stats">
            <div className="sync-stat">
              <span className="stat-label">Groups Found:</span>
              <span className="stat-value">{syncResults.groups_found_in_azure}</span>
            </div>
            <div className="sync-stat">
              <span className="stat-label">Groups Synced:</span>
              <span className="stat-value">{syncResults.groups_synced}</span>
            </div>
            <div className="sync-stat">
              <span className="stat-label">Members Added:</span>
              <span className="stat-value added">{syncResults.members_added}</span>
            </div>
            <div className="sync-stat">
              <span className="stat-label">Members Removed:</span>
              <span className="stat-value removed">{syncResults.members_removed}</span>
            </div>
            <div className="sync-stat">
              <span className="stat-label">Members Unchanged:</span>
              <span className="stat-value">{syncResults.members_unchanged}</span>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Filter by Role:</label>
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
          >
            <option value="">All Roles</option>
            {roles.map(role => (
              <option key={role.role_id} value={role.role_id}>
                {role.role_name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Show Inactive Groups
          </label>
        </div>
      </div>

      {/* Groups Table */}
      <div className="groups-table-container">
        {groups.length === 0 ? (
          <div className="empty-state">
            <p>No groups found. Create a group mapping to get started.</p>
          </div>
        ) : (
          <table className="groups-table">
            <thead>
              <tr>
                <th>Group Name</th>
                <th>Mapped Role</th>
                <th>Members</th>
                <th>Status</th>
                <th>Auto-Provision</th>
                <th>Sync Enabled</th>
                <th>Last Sync</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <tr key={group.group_id} className={!group.is_active ? 'inactive-row' : ''}>
                  <td>
                    <div className="group-name-cell">
                      <strong>{group.group_name}</strong>
                      {group.description && (
                        <div className="group-description">{group.description}</div>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={getRoleBadgeClass(group.mapped_role_id)}>
                      {group.role_name || group.mapped_role_id}
                    </span>
                  </td>
                  <td>
                    <button
                      className="btn-link"
                      onClick={() => openMembersModal(group)}
                    >
                      {group.member_count} {group.member_count === 1 ? 'member' : 'members'}
                    </button>
                  </td>
                  <td>
                    <span className={`status-badge ${group.is_active ? 'active' : 'inactive'}`}>
                      {group.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <span className={group.auto_provision ? 'text-success' : 'text-muted'}>
                      {group.auto_provision ? '✓' : '✗'}
                    </span>
                  </td>
                  <td>
                    <span className={group.sync_enabled ? 'text-success' : 'text-muted'}>
                      {group.sync_enabled ? '✓' : '✗'}
                    </span>
                  </td>
                  <td className="date-cell">
                    {formatDate(group.last_sync_date)}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon"
                        onClick={() => openEditModal(group)}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => handleTriggerSync(group.group_id)}
                        title="Sync"
                        disabled={syncing}
                      >
                        🔄
                      </button>
                      <button
                        className="btn-icon btn-danger"
                        onClick={() => openDeleteModal(group)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Group Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Group Mapping</h2>
              <button
                className="modal-close"
                onClick={() => setShowCreateModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCreateGroup}>
              <div className="form-group">
                <label>Azure AD Group ID *</label>
                <input
                  type="text"
                  value={formData.group_id}
                  onChange={(e) => setFormData({ ...formData, group_id: e.target.value })}
                  required
                  placeholder="e.g., 18c2b1ce-c124-4576-a7dc-62054098d961"
                />
                <small>The Object ID from Azure AD</small>
              </div>

              <div className="form-group">
                <label>Group Name *</label>
                <input
                  type="text"
                  value={formData.group_name}
                  onChange={(e) => setFormData({ ...formData, group_name: e.target.value })}
                  required
                  placeholder="e.g., DAMA_Campaign_Admins"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Mapped Role *</label>
                <select
                  value={formData.mapped_role_id}
                  onChange={(e) => setFormData({ ...formData, mapped_role_id: e.target.value })}
                  required
                >
                  <option value="">Select a role...</option>
                  {roles.map(role => (
                    <option key={role.role_id} value={role.role_id}>
                      {role.role_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group-row">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  Active
                </label>

                <label>
                  <input
                    type="checkbox"
                    checked={formData.auto_provision}
                    onChange={(e) => setFormData({ ...formData, auto_provision: e.target.checked })}
                  />
                  Auto-Provision
                </label>

                <label>
                  <input
                    type="checkbox"
                    checked={formData.sync_enabled}
                    onChange={(e) => setFormData({ ...formData, sync_enabled: e.target.checked })}
                  />
                  Sync Enabled
                </label>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create Group
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Group Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Group Mapping</h2>
              <button
                className="modal-close"
                onClick={() => setShowEditModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleUpdateGroup}>
              <div className="form-group">
                <label>Group ID (Read-Only)</label>
                <input
                  type="text"
                  value={formData.group_id}
                  disabled
                  className="readonly-field"
                />
              </div>

              <div className="form-group">
                <label>Group Name</label>
                <input
                  type="text"
                  value={formData.group_name}
                  onChange={(e) => setFormData({ ...formData, group_name: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Mapped Role</label>
                <select
                  value={formData.mapped_role_id}
                  onChange={(e) => setFormData({ ...formData, mapped_role_id: e.target.value })}
                >
                  {roles.map(role => (
                    <option key={role.role_id} value={role.role_id}>
                      {role.role_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group-row">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  Active
                </label>

                <label>
                  <input
                    type="checkbox"
                    checked={formData.auto_provision}
                    onChange={(e) => setFormData({ ...formData, auto_provision: e.target.checked })}
                  />
                  Auto-Provision
                </label>

                <label>
                  <input
                    type="checkbox"
                    checked={formData.sync_enabled}
                    onChange={(e) => setFormData({ ...formData, sync_enabled: e.target.checked })}
                  />
                  Sync Enabled
                </label>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Update Group
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Members Modal */}
      {showMembersModal && selectedGroup && (
        <div className="modal-overlay" onClick={() => setShowMembersModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Members of {selectedGroup.group_name}</h2>
              <button
                className="modal-close"
                onClick={() => setShowMembersModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {groupMembers.length === 0 ? (
                <p className="empty-state">No members found in this group.</p>
              ) : (
                <table className="members-table">
                  <thead>
                    <tr>
                      <th>Display Name</th>
                      <th>Employee ID</th>
                      <th>Email</th>
                      <th>Department</th>
                      <th>Status</th>
                      <th>Last Verified</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupMembers.map((member) => (
                      <tr key={member.user_id}>
                        <td><strong>{member.display_name}</strong></td>
                        <td>{member.employee_id}</td>
                        <td className="email-cell">{member.email}</td>
                        <td>{member.department || 'N/A'}</td>
                        <td>
                          <span className={`status-badge ${member.is_active ? 'active' : 'inactive'}`}>
                            {member.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="date-cell">
                          {formatDate(member.last_verified_date)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowMembersModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedGroup && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Group Mapping</h2>
              <button
                className="modal-close"
                onClick={() => setShowDeleteModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete the group mapping for:</p>
              <p className="delete-group-name"><strong>{selectedGroup.group_name}</strong></p>
              <p className="warning-text">
                This will deactivate the group mapping and all its memberships.
                The group will no longer grant permissions to users.
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={() => handleDeleteGroup(false)}
              >
                Deactivate Group
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AzureADGroups;
